/**
 * CallNote: the office's weekly call note on the care manager's row. The
 * words as typed, wrapped, line breaks kept, in a block at most four lines
 * tall that scrolls inside itself. When the note runs past four lines a
 * quiet "Show all" lets the block grow in place to at most twelve lines,
 * still scrolling inside, never the page; "Show less" takes it back. The
 * row is never taller than that.
 */

import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { TextAction } from '@/components/workspace/TextAction';
import { NOTE_LINES_AT_REST, NOTE_LINES_EXPANDED } from '@/lib/callMentions';

/** leading-6: 1.5rem a line. Written out whole so Tailwind finds them. */
const AT_REST = 'max-h-[6rem]';
const EXPANDED = 'max-h-[18rem]';

/** A note longer than four lines without layout to measure (a first
 *  paint, or a test): more line breaks than the block holds, or more
 *  words than four lines of the narrowest Notes column carry. */
function looksLong(note: string): boolean {
  return note.split('\n').length > NOTE_LINES_AT_REST || note.length > 240;
}

export function CallNote({ note, rosterId }: { note: string; rosterId: string }): ReactNode {
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(() => looksLong(note));
  const block = useRef<HTMLParagraphElement | null>(null);

  useLayoutEffect(() => {
    const el = block.current;
    if (!el || el.clientHeight === 0) return;
    if (!expanded) setOverflows(el.scrollHeight > el.clientHeight + 1);
  }, [note, expanded]);

  return (
    <div className="min-w-0" data-testid={`availability-note-wrap-${rosterId}`}>
      <p
        ref={block}
        data-testid={`availability-note-${rosterId}`}
        data-lines={expanded ? NOTE_LINES_EXPANDED : NOTE_LINES_AT_REST}
        // A block that scrolls is reachable by keyboard.
        tabIndex={overflows ? 0 : undefined}
        className={`${expanded ? EXPANDED : AT_REST} overflow-y-auto overscroll-contain whitespace-pre-wrap break-words text-sm leading-6 text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 rounded-tight`}
      >
        {note}
      </p>
      {(overflows || expanded) && (
        <TextAction
          className="-ml-2 mt-0.5"
          aria-expanded={expanded}
          data-testid={`availability-note-toggle-${rosterId}`}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? 'Show less' : 'Show all'}
        </TextAction>
      )}
    </div>
  );
}
